import {
  DropdownNavList,
  Icon,
  Input,
  NavItem,
  NavListItemWithBlockDescription,
  NavListItemWithBlockDescriptionValue,
} from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSegment } from '../../../../../hooks'
import { useIntercomArticlesSearch } from '../../hooks/useIntercomArticlesSearch'
import { Article } from '../../types/article'
import * as Styled from './styles'

const MIN_CHARS_TO_SHOW_HITS = 3

export const ArticlesSearch = (): ReactElement => {
  const [areHitsVisible, setAreHitsVisible] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { articles, isLoading: isSearching, setSearchQuery } = useIntercomArticlesSearch()

  const hitItems: NavItem<NavListItemWithBlockDescriptionValue>[] = articles.map((article: Article) => ({
    id: article.url,
    value: {
      displayData: {
        title: article.title,
      },
    },
    children: article.title,
    href: article.url,
    target: '_blank',
    rel: 'noopener noreferrer',
  }))

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const isQueryLongerThanMinimumCharsAmount = value.length >= MIN_CHARS_TO_SHOW_HITS
      if (isQueryLongerThanMinimumCharsAmount) {
        setSearchQuery(value)
        setAreHitsVisible(true)
        // eslint-disable-next-line @typescript-eslint/naming-convention
        track('xxx-help-modal-article-search', { article_selected: false })
      } else {
        setAreHitsVisible(false)
        setSearchQuery(null)
      }
    },
    [setSearchQuery, track],
  )

  const handleClear = useCallback(() => {
    setAreHitsVisible(false)
    setSearchQuery(null)
  }, [setSearchQuery])

  const handleItemClick = useCallback(
    (articleLink: string) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      track('xxx-help-modal-article-search', { article_selected: true, article_link: articleLink })
    },
    [track],
  )

  return (
    <Styled.ArticleWrapper>
      <DropdownNavList
        autoToggle={false}
        items={hitItems}
        itemRender={(props) => <NavListItemWithBlockDescription {...props} />}
        isOpen={areHitsVisible}
        isFetching={isSearching}
        onClose={handleClear}
        onItemClick={handleItemClick}
        placement="bottom-start"
        size="fitTrigger"
        trigger={
          <Input
            allowClear
            onClear={handleClear}
            onChangeDebounced={handleOnChange}
            placeholder={t('help.modal.form.search.placeholder')}
            prefix={<Icon icon="search" />}
          />
        }
      />
    </Styled.ArticleWrapper>
  )
}
